import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkle,
  ChartLineUp,
  UsersThree,
  FilmSlate,
  Star,
  Pulse,
  ChatCircleText,
} from "@phosphor-icons/react";
import { Sidebar } from "../components/Sidebar";
import { listRecces, getOwnerStats, adminSetPlan } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Stats() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recces, setRecces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      const [s, r] = await Promise.all([getOwnerStats(), listRecces().catch(() => [])]);
      setData(s);
      setRecces(r);
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Owner only." : "Could not load stats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  // Block non-owners loudly
  if (user && !user.is_owner) {
    return (
      <div className="flex min-h-screen">
        <Sidebar onNew={() => (window.location.href = "/")} recces={recces} />
        <main className="flex-1 p-12" data-testid="stats-not-owner">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            recce · admin
          </p>
          <h1 className="font-serif text-5xl mt-3">This room's not for you.</h1>
          <p className="mt-3 text-ink-muted">Owner-only page.</p>
          <Link to="/" className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.18em] underline">
            ← back to scouts
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar onNew={() => (window.location.href = "/")} recces={recces} />

      <main className="flex-1 px-6 md:px-12 py-8 md:py-12 max-w-[1500px]" data-testid="stats-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted hover:text-ink"
          data-testid="stats-back-link"
        >
          <ArrowLeft size={14} /> all scouts
        </Link>

        <header className="border-b border-ink pb-8 mt-6 mb-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-rust">
            <Sparkle size={11} weight="fill" className="inline mr-1" />
            owner only · admin desk
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] mt-3">
            Who is using<br />
            <em className="not-italic text-rust">your studio.</em>
          </h1>
          <p className="mt-5 text-ink-muted max-w-xl leading-relaxed">
            Live counts. Refreshes every 15 seconds. No tracking pixels, no third parties — just
            what's already in the database, presented honestly.
          </p>
        </header>

        {loading && (
          <p className="font-mono text-xs text-ink-muted">loading…</p>
        )}
        {error && (
          <p className="font-mono text-xs text-rust" data-testid="stats-error">
            {error}
          </p>
        )}

        {data && (
          <>
            {/* Big-number cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-rule mb-4 border border-rule">
              <BigNumber
                icon={UsersThree}
                label="total people"
                value={data.users.total}
                hint={`${data.users.new_24h} new in 24h · ${data.users.new_7d} this week`}
                testid="stat-users-total"
              />
              <BigNumber
                icon={FilmSlate}
                label="recces"
                value={data.recces.total}
                hint={`${data.recces.ready} ready · ${data.recces.scouting} scouting · ${data.recces.archived} archived`}
                testid="stat-recces-total"
              />
              <BigNumber
                icon={ChartLineUp}
                label="references"
                value={data.references.total}
                hint={`${data.references.web} web · ${data.references.image} img · ${data.references.memory} memory`}
                testid="stat-refs-total"
              />
              <BigNumber
                icon={Star}
                label="starred"
                value={data.references.starred}
                hint={`${data.recces.watching} recces watching`}
                testid="stat-starred"
              />
            </section>

            {/* Activity row — DAU/WAU + plans + feedback breakdown */}
            <section className="grid grid-cols-2 md:grid-cols-5 gap-px bg-rule mb-12 border border-rule">
              <BigNumber
                icon={Pulse}
                label="active · 24h"
                value={data.users.dau ?? 0}
                hint="people who hit the studio in the last day"
                testid="stat-dau"
                tone="rust"
              />
              <BigNumber
                icon={Pulse}
                label="active · 7d"
                value={data.users.wau ?? 0}
                hint="people who hit the studio this week"
                testid="stat-wau"
              />
              <BigNumber
                icon={UsersThree}
                label="plan · free"
                value={data.plans?.free ?? 0}
                hint={`${data.plans?.paid ?? 0} on paid`}
                testid="stat-plan-free"
              />
              <BigNumber
                icon={Sparkle}
                label="sparks"
                value={data.references?.feedback_spark ?? 0}
                hint={`${data.references?.feedback_up ?? 0} up · ${data.references?.feedback_down ?? 0} down`}
                testid="stat-sparks"
                tone="rust"
              />
              <BigNumber
                icon={ChatCircleText}
                label="feedback notes"
                value={data.feedback_total ?? 0}
                hint={`${(data.feedback || []).length} most recent shown below`}
                testid="stat-feedback-total"
              />
            </section>

            {/* Two columns */}
            <section className="grid lg:grid-cols-2 gap-10">
              {/* Recent users */}
              <div data-testid="recent-users-panel">
                <div className="flex items-baseline justify-between border-b border-rule pb-3 mb-5">
                  <h2 className="font-serif text-2xl">Recent signups</h2>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                    {data.users.recent.length} of {data.users.total}
                  </span>
                </div>
                {data.users.recent.length === 0 ? (
                  <p className="font-mono text-xs text-ink-muted">No users yet.</p>
                ) : (
                  <ul className="divide-y divide-rule border-y border-rule">
                    {data.users.recent.map((u, i) => (
                      <li
                        key={u.email + i}
                        className="flex items-center gap-3 py-3"
                        data-testid={`user-row-${i}`}
                      >
                        {u.picture ? (
                          <img
                            src={u.picture}
                            alt=""
                            className="w-8 h-8 border border-rule object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          <div className="w-8 h-8 border border-rule bg-bone-deep flex items-center justify-center font-serif text-base">
                            {(u.name || u.email).slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-serif text-base leading-tight truncate">
                            {u.name || "—"}
                            {u.is_guest && (
                              <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.18em] text-ink-muted border border-rule px-1.5 py-0.5">
                                guest
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-ink-muted truncate">
                            {u.email}
                          </div>
                        </div>
                        <PlanToggle
                          user={u}
                          onChanged={refresh}
                          data-testid={`plan-toggle-${i}`}
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted whitespace-nowrap">
                          {fmtRel(u.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Top recces */}
              <div data-testid="top-recces-panel">
                <div className="flex items-baseline justify-between border-b border-rule pb-3 mb-5">
                  <h2 className="font-serif text-2xl">Most-referenced recces</h2>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                    by ref count
                  </span>
                </div>
                {data.top_recces.length === 0 ? (
                  <p className="font-mono text-xs text-ink-muted">No recces yet.</p>
                ) : (
                  <ul className="divide-y divide-rule border-y border-rule">
                    {data.top_recces.map((r) => (
                      <li
                        key={r.id}
                        className="py-3 flex items-baseline gap-4"
                        data-testid={`top-recce-${r.id}`}
                      >
                        <div className="font-mono text-2xl text-rust tabular-nums w-10 shrink-0">
                          {r.ref_count}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-serif text-lg leading-tight truncate">
                            {r.title}
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                            {r.medium}
                            {r.persona && (
                              <>
                                {" · "}
                                <span className="text-rust normal-case font-serif italic text-sm">
                                  {r.persona}
                                </span>
                              </>
                            )}
                            {" · "}
                            {r.owner_email}
                            {r.watching && <span className="text-rust"> · ◉ watching</span>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Feedback inbox */}
            <section className="mt-12" data-testid="feedback-panel">
              <div className="flex items-baseline justify-between border-b border-rule pb-3 mb-5">
                <h2 className="font-serif text-2xl">Notes from the audience</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                  {(data.feedback || []).length} of {data.feedback_total ?? 0}
                </span>
              </div>
              {(data.feedback || []).length === 0 ? (
                <p className="font-mono text-xs text-ink-muted">
                  No feedback yet. The "Send feedback" link in the sidebar lets anyone drop a note.
                </p>
              ) : (
                <ul className="space-y-4">
                  {data.feedback.map((f) => (
                    <li
                      key={f.id}
                      className="border border-rule bg-paper px-5 py-4"
                      data-testid={`feedback-row-${f.id}`}
                    >
                      <p className="font-serif text-lg leading-snug whitespace-pre-line">
                        {f.message}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                        <span>{f.user_name || "anon"}</span>
                        <span>·</span>
                        <span className="normal-case tracking-normal">{f.user_email || "—"}</span>
                        {f.page && (
                          <>
                            <span>·</span>
                            <span className="normal-case tracking-normal text-rust">{f.page}</span>
                          </>
                        )}
                        <span>·</span>
                        <span>{fmtRel(f.created_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              generated {new Date(data.generated_at).toLocaleString("en-GB", { hour12: false })}
            </p>
          </>
        )}
      </main>
    </div>
  );
}

const BigNumber = ({ icon: Icon, label, value, hint, testid, tone }) => (
  <div className="bg-paper p-6" data-testid={testid}>
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
      <Icon size={12} weight="thin" />
      {label}
    </div>
    <div
      className={`font-serif text-6xl leading-none mt-3 tabular-nums ${
        tone === "rust" ? "text-rust" : ""
      }`}
    >
      {String(value).padStart(2, "0")}
    </div>
    <div className="font-mono text-[10px] text-ink-muted mt-2 leading-relaxed">
      {hint}
    </div>
  </div>
);

const PlanToggle = ({ user, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const plan = user.plan || "free";
  const next = plan === "paid" ? "free" : "paid";
  const flip = async () => {
    if (!user.user_id || busy) return;
    setBusy(true);
    try {
      await adminSetPlan(user.user_id, next);
      await onChanged?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={flip}
      disabled={busy || !user.user_id}
      data-testid={`plan-toggle-${user.user_id || "unknown"}`}
      title={`Switch to ${next}`}
      className={`font-mono text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 border transition-colors whitespace-nowrap ${
        plan === "paid"
          ? "border-rust text-rust hover:bg-rust hover:text-paper"
          : "border-rule text-ink-muted hover:border-ink hover:text-ink"
      } ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
    >
      {busy ? "…" : plan}
    </button>
  );
};

function fmtRel(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}
